import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DropDown from "../DropDown/DropDown";
import CheckboxList from "../../CheckboxList/CheckboxList";
import SearchInput from "../SearchInput/SearchInput";
import { Option } from "@/interfaces/Option";
import DropdownClearButton from "../Utilities/DropDownClear/DropdownClearButton";

export interface SelectProps {
  optionList: Option[];
  typeOfField: string;
  isLoading?: boolean;
  onSelect: (value: string[]) => void;
  valueOfField: string[];
  dataTestID?: string;
  id?: string;
  handleSelectNone: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}
const Select: React.FC<SelectProps> = ({
  dataTestID,
  optionList,
  typeOfField,
  isLoading,
  onSelect,
  valueOfField,
  id,
  handleSelectNone,
  onKeyDown,
}) => {
  const [isDropdown, setIsDropdown] = useState<boolean>(false);
  const [selectedFieldValue, setSelectedFieldValue] =
    useState<Option[]>(optionList);
  const [inputValue, setInputValue] = useState("");
  const [isTyped, setIsTyped] = useState<boolean>(false);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLDivElement>(null);
  const combinedRef = (node: HTMLDivElement) => {
    (dropdownRef as React.MutableRefObject<HTMLDivElement | null>).current =
      node; //outside click logic
    (focusRef as React.MutableRefObject<HTMLDivElement | null>).current = node; //clear button focus
  };

  useEffect(() => {
    setSelectedFieldValue(optionList);
  }, [optionList]);

  useEffect(() => {
    if (valueOfField.length === 0) {
      setIsSelectAll(false);
    } else if (
      valueOfField.length === optionList.length &&
      optionList.length > 0
    ) {
      setIsSelectAll(true);
    }
  }, [valueOfField, optionList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdown(false); // Collapse dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCheckboxChange = (value: string): void => {
    const formattedValue = value.toLowerCase();
    const updatedSelection = valueOfField.includes(formattedValue)
      ? valueOfField.filter((opt: string) => opt !== formattedValue)
      : [...valueOfField, formattedValue];
    if (isSelectAll) {
      setIsSelectAll(false);
    }
    if (updatedSelection.length === optionList.length) {
      setIsSelectAll(true);
    }
    onSelect(updatedSelection);
  };
  const handleSelectAll = () => {
    setIsSelectAll(true);
    onSelect(optionList.map((e: Option) => e.value.toLowerCase()));
  };

  const handleInternalSelectNone = () => {
    setIsSelectAll(false);
    onSelect([]);
  };

  const toggleDropdown = () => {
    setIsDropdown((prev) => !prev);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    const searchValue = event.target.value.toUpperCase();
    setIsTyped(true);
    setSelectedFieldValue(
      optionList.filter((e: Option) => e.label.includes(searchValue)),
    );
  };
  const controlText = useMemo(
    () => `Select ${isSelectAll ? "None" : "All"}`,
    [isSelectAll],
  );
  const hasSelection = valueOfField && valueOfField.length > 0;
  const handleClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsSelectAll(false);
    setInputValue("");
    setIsTyped(false);
    setSelectedFieldValue(optionList);
    onSelect([]);
    if (handleSelectNone) handleSelectNone();
    focusRef.current?.focus();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      setIsDropdown(false);
    }
    return onKeyDown && onKeyDown(e);
  };

  return (
    <div
      ref={combinedRef}
      className="w-full h-full relative"
      onKeyDown={handleKeyDown}
    >
      <DropDown
        isDropdown={isDropdown}
        dataTestID={dataTestID ?? ""}
        toggleDropdown={toggleDropdown}
        valueOfField={valueOfField}
        optionList={optionList}
        id={id}
      >
        <div>
          {typeOfField === "COMMODITY" && (
            <div className="relative h-10">
              <SearchInput
                placeholder={`Search commodities...`}
                value={inputValue}
                onChange={handleInputChange}
              />
              {inputValue.length > 0 && (
                <button
                  className="absolute end-3 top-3 h-6 w-6 pt-[2px] rounded-full bg-gray-600"
                  onClick={() => {
                    setInputValue("");
                    setIsTyped(false);
                  }}
                >
                  <span className="icon-close text-white text-xl" />
                </button>
              )}
            </div>
          )}
          {optionList.length > 0 && (
            <div className="pl-2 pt-2 text-left font-semibold text-CostcoAction">
              <button
                onClick={
                  controlText === "Select All"
                    ? handleSelectAll
                    : handleInternalSelectNone
                }
              >
                {controlText}
              </button>
            </div>
          )}
          <CheckboxList
            options={
              isTyped && inputValue !== "" ? selectedFieldValue : optionList
            }
            valueOfField={valueOfField}
            handleCheckboxChange={handleCheckboxChange}
            isLoading={isLoading}
            isAll={isSelectAll}
          />
        </div>
      </DropDown>
      {!isLoading && hasSelection && !isDropdown && (
        <DropdownClearButton
          onClear={handleClearClick}
          dataTestID={`clear-${id}-selection-button`}
        />
      )}
    </div>
  );
};

export default Select;
